import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import { useProtectedFileUrl } from '../utils/protectedFiles';
import './UserProfile.css';

const UserProfile = () => {
    const navigate = useNavigate();
    const { updateUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: ''
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const { objectUrl: profilePhotoUrl } = useProtectedFileUrl(profile?.profilePhoto);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const response = await axiosInstance.get('/api/profile');

            if (response.data.success) {
                setProfile(response.data.user);
                // Update AuthContext with latest user data
                updateUser(response.data.user);
                setFormData({
                    fullName: response.data.user.fullName || '',
                    email: response.data.user.email || '',
                    phone: response.data.user.phone || ''
                });
            } else {
                setError(response.data.message || 'Failed to fetch profile');
            }
        } catch (err) {
            setError('Error fetching profile');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handlePasswordChange = (e) => {
        setPasswordData({
            ...passwordData,
            [e.target.name]: e.target.value
        });
    };

    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                setError('Photo size must be less than 2MB');
                return;
            }
            setPhotoFile(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            const response = await axiosInstance.put('/api/profile', formData);

            if (response.data.success) {
                setProfile(response.data.user);
                // Update AuthContext with latest user data
                updateUser(response.data.user);
                setSuccess('Profile updated successfully');
                setIsEditing(false);
            } else {
                setError(response.data.message || 'Failed to update profile');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error updating profile');
            console.error(err);
        }
    };

    const handleUploadPhoto = async () => {
        if (!photoFile) {
            setError('Please select a photo');
            return;
        }

        setError('');
        setSuccess('');

        try {
            const formData = new FormData();
            formData.append('photo', photoFile);

            const response = await axiosInstance.post('/api/profile/photo', formData);

            if (response.data.success) {
                setSuccess('Photo uploaded successfully');
                setPhotoFile(null);
                setPhotoPreview(null);
                // Fetch updated profile which will trigger updateUser
                await fetchProfile();
            } else {
                setError(response.data.message || 'Failed to upload photo');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error uploading photo');
            console.error(err);
        }
    };

    const handleDeletePhoto = async () => {
        if (!window.confirm('Are you sure you want to delete your profile photo?')) {
            return;
        }

        setError('');
        setSuccess('');

        try {
            const response = await axiosInstance.delete('/api/profile/photo');

            if (response.data.success) {
                setSuccess('Photo deleted successfully');
                // Fetch updated profile which will trigger updateUser
                await fetchProfile();
            } else {
                setError(response.data.message || 'Failed to delete photo');
            }
        } catch (err) {
            setError('Error deleting photo');
            console.error(err);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setError('New password must be at least 6 characters');
            return;
        }

        try {
            const response = await axiosInstance.put('/api/profile/password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });

            if (response.data.success) {
                setSuccess('Password changed successfully');
                setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                setIsChangingPassword(false);
            } else {
                setError(response.data.message || 'Failed to change password');
            }
        } catch (err) {
            setError('Error changing password');
            console.error(err);
        }
    };

    if (loading) {
        return <div className="profile-loading">Loading profile...</div>;
    }

    return (
        <div className="user-profile-container">
            <div className="profile-header">
                <button 
                    onClick={() => navigate('/purpose-selection')} 
                    className="btn btn-back"
                    title="Back to Purpose Selection"
                >
                    ← Back
                </button>
                <h2>My Profile</h2>
            </div>

            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            <div className="profile-content">
                {/* Profile Photo Section */}
                <div className="profile-photo-section">
                    <div className="photo-display">
                        {photoPreview ? (
                            <img src={photoPreview} alt="Preview" className="profile-photo" />
                        ) : profile?.profilePhoto && profilePhotoUrl ? (
                            <img src={profilePhotoUrl} alt="Profile" className="profile-photo" />
                        ) : (
                            <div className="profile-photo-placeholder">
                                <span>{profile?.fullName?.charAt(0).toUpperCase()}</span>
                            </div>
                        )}
                    </div>
                    
                    <div className="photo-actions">
                        <input
                            type="file"
                            id="photo-input"
                            accept="image/*"
                            onChange={handlePhotoSelect}
                            style={{ display: 'none' }}
                        />
                        <label htmlFor="photo-input" className="btn btn-secondary">
                            Choose Photo
                        </label>
                        {photoFile && (
                            <button onClick={handleUploadPhoto} className="btn btn-primary">
                                Upload
                            </button>
                        )}
                        {profile?.profilePhoto && (
                            <button onClick={handleDeletePhoto} className="btn btn-danger">
                                Delete Photo
                            </button>
                        )}
                    </div>
                </div>

                {/* Profile Information Section */}
                <div className="profile-info-section">
                    <div className="section-header">
                        <h3>Profile Information</h3>
                        {!isEditing && (
                            <button onClick={() => setIsEditing(true)} className="btn btn-secondary">
                                Edit Profile
                            </button>
                        )}
                    </div>

                    {isEditing ? (
                        <form onSubmit={handleUpdateProfile} className="profile-form">
                            <div className="form-group">
                                <label>Full Name</label>
                                <input
                                    type="text"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="form-group">
                                <label>Phone</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary">
                                    Save Changes
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsEditing(false);
                                        setFormData({
                                            fullName: profile.fullName || '',
                                            email: profile.email || '',
                                            phone: profile.phone || ''
                                        });
                                    }}
                                    className="btn btn-secondary"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="profile-details">
                            <div className="detail-row">
                                <span className="detail-label">Username:</span>
                                <span className="detail-value">{profile?.username}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Full Name:</span>
                                <span className="detail-value">{profile?.fullName}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Email:</span>
                                <span className="detail-value">{profile?.email || 'Not set'}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Phone:</span>
                                <span className="detail-value">{profile?.phone || 'Not set'}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Role:</span>
                                <span className="detail-value role-badge">{profile?.role}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Company:</span>
                                <span className="detail-value">{profile?.company?.name || 'Not assigned'}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Branch:</span>
                                <span className="detail-value">{profile?.branch?.name || 'Not assigned'}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Department:</span>
                                <span className="detail-value">{profile?.department?.name || 'Not assigned'}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Password Change Section */}
                <div className="password-section">
                    <div className="section-header">
                        <h3>Change Password</h3>
                        {!isChangingPassword && (
                            <button onClick={() => setIsChangingPassword(true)} className="btn btn-secondary">
                                Change Password
                            </button>
                        )}
                    </div>

                    {isChangingPassword && (
                        <form onSubmit={handleChangePassword} className="password-form">
                            <div className="form-group">
                                <label>Current Password</label>
                                <input
                                    type="password"
                                    name="currentPassword"
                                    value={passwordData.currentPassword}
                                    onChange={handlePasswordChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>New Password</label>
                                <input
                                    type="password"
                                    name="newPassword"
                                    value={passwordData.newPassword}
                                    onChange={handlePasswordChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Confirm New Password</label>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    value={passwordData.confirmPassword}
                                    onChange={handlePasswordChange}
                                    required
                                />
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary">
                                    Update Password
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsChangingPassword(false);
                                        setPasswordData({
                                            currentPassword: '',
                                            newPassword: '',
                                            confirmPassword: ''
                                        });
                                    }}
                                    className="btn btn-secondary"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
